import React from "react";
import { useParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";

const CONTENT = {
    privacy: {
        title: "Privacy Policy",
        intro: "Your trust means everything to us. This policy explains what we collect and how we use it.",
        sections: [
            ["Information we collect", "We collect your name, contact details, delivery address and order history to fulfill your orders. Payment details are handled securely by our payment processor (Stripe) and never stored on our servers."],
            ["How we use it", "We use your information to process orders, provide support, send order updates, and — only with your consent — share news, offers and new-flavor announcements."],
            ["Sharing", "We never sell your data. We share information only with trusted providers (payment, delivery, email/SMS) strictly to operate our service."],
            ["Your choices", "You can update your email and SMS preferences anytime in your account dashboard, or unsubscribe from any message."],
        ],
    },
    terms: {
        title: "Terms & Conditions",
        intro: "By placing an order with Sweet Pearls you agree to the following terms.",
        sections: [
            ["Orders", "All orders are subject to availability. Prices are shown in USD and may change. We reserve the right to cancel any order due to product availability or errors in pricing."],
            ["Fulfillment", "Pickup, local delivery and shipping availability depend on your location and selected date. Fresh products are perishable and should be enjoyed promptly."],
            ["Payment", "Payment is processed securely at checkout. You represent that you are authorized to use the payment method provided."],
            ["Liability", "Sweet Pearls is not liable for issues arising from improper storage after delivery or pickup, or from undisclosed allergies."],
        ],
    },
    refund: {
        title: "Refund & Cancellation Policy",
        intro: "Because our products are freshly handcrafted and perishable, please review our policy carefully.",
        sections: [
            ["Cancellations", "Orders may be cancelled for a full refund up to 24 hours before the scheduled fulfillment date. Cancellations after that window may not be eligible for a refund as preparation has begun."],
            ["Refunds", "If something isn't right with your order, contact us within 24 hours of receipt with photos and we'll make it right — a replacement or refund at our discretion."],
            ["Perishable items", "Due to food-safety regulations, we cannot accept returns of perishable products."],
            ["Processing", "Approved refunds are issued to your original payment method within 5–10 business days."],
        ],
    },
    shipping: {
        title: "Shipping, Pickup & Delivery Policy",
        intro: "Here's how you can get your Sweet Pearls, fresh and chilled.",
        sections: [
            ["Local pickup", "Pickup is free and typically ready within 2 hours at our kitchen. You'll choose a pickup date at checkout."],
            ["Local delivery", "Same-day and scheduled delivery is available within our local service area. Delivery fees may apply and orders over $50 ship free."],
            ["Nationwide shipping", "We ship chilled, typically arriving within 2–3 days. We package with insulation and ice packs to keep everything fresh."],
            ["Fulfillment dates", "Available pickup and delivery dates are shown at checkout. We prepare everything fresh for your selected date."],
        ],
    },
};

export default function Policy() {
    const { type } = useParams();
    const c = CONTENT[type] || CONTENT.privacy;

    return (
        <div className="mx-auto max-w-3xl px-5 py-16">
            <Reveal>
                <h1 className="font-serif text-4xl font-semibold text-roast sm:text-5xl">{c.title}</h1>
                <p className="mt-3 text-mocha">{c.intro}</p>
            </Reveal>
            <div className="mt-10 space-y-8">
                {c.sections.map(([h, b], i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <div className="rounded-2xl border border-caramel/10 bg-white p-6 shadow-warm">
                            <h2 className="font-serif text-xl text-roast">{h}</h2>
                            <p className="mt-2 leading-relaxed text-mocha">{b}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
            <p className="mt-10 text-sm text-mocha">Last updated {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}. Questions? Visit our Contact page.</p>
        </div>
    );
}
